/**
 * Pure presentation helpers for the Dashboard workspace.
 *
 * These exist to make the Dashboard honest and diagnosable without touching any
 * business authority:
 *  - `serializeError` extracts the real signal from a PostgREST error object
 *    (whose `message` is often empty while `code`/`details`/`hint` carry the
 *    truth), so a failure is never logged as an empty `{"message":""}`.
 *  - `isTransientError` recognises both `Error` instances and PostgREST plain
 *    error objects, restoring the intended retry behaviour for network/serve
 *    failures.
 *  - `attentionDegraded` tells the Dashboard whether its "all clear" claim is
 *    trustworthy given which queries failed.
 *  - `describeDashboardError` maps internal query labels to operator language.
 */

const TRANSIENT_PATTERN =
  /internal server error|service unavailable|too many requests|failed to fetch|network|timeout|econnreset|etimedout|fetch failed|connection reset|\b502\b|\b503\b|\b504\b|\b429\b/i;

/**
 * Produce a human-readable string from any thrown/returned error shape.
 * PostgREST errors are plain objects (not `Error` instances) with `message`,
 * `code`, `details` and `hint`; an empty `message` must not hide the rest.
 */
export function serializeError(err: unknown): string {
  if (err == null) return "Unknown error";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message || err.name;
  if (typeof err === "object") {
    const o = err as Record<string, unknown>;
    const parts: string[] = [];
    if (typeof o.message === "string" && o.message.length > 0) parts.push(o.message);
    if (typeof o.code === "string" && o.code.length > 0) parts.push(`code=${o.code}`);
    if (typeof o.details === "string" && o.details.length > 0) parts.push(`details=${o.details}`);
    if (typeof o.hint === "string" && o.hint.length > 0) parts.push(`hint=${o.hint}`);
    if (typeof o.status === "number") parts.push(`status=${o.status}`);
    if (parts.length > 0) return parts.join("; ");
    try {
      return JSON.stringify(o);
    } catch {
      return String(o);
    }
  }
  return String(err);
}

/**
 * Whether an error is transient (worth a bounded retry) rather than a
 * deterministic permission/schema failure. Previously `err instanceof Error`
 * was the only branch, so PostgREST plain objects were never recognised and
 * retries silently never ran.
 */
export function isTransientError(err: unknown): boolean {
  return TRANSIENT_PATTERN.test(serializeError(err));
}

/**
 * Query labels whose failure invalidates the Dashboard's "nothing needs your
 * attention" claim. These are exactly the queries the attention list is derived
 * from; non-attention queries (invitations, memberships, customers, suppliers,
 * locations, connectors, orgSettings) do not gate the all-clear state.
 */
export const ATTENTION_QUERY_LABELS: readonly string[] = [
  "products",
  "balances",
  "pos",
  "sos",
  "returns",
  "shipments",
  "soItems",
  "ledger",
  "exceptions",
  "automationLogs",
  "anomalies",
];

/** True when at least one query that feeds the attention list has failed. */
export function attentionDegraded(partialErrors: readonly string[]): boolean {
  const failed = new Set(partialErrors);
  return ATTENTION_QUERY_LABELS.some((label) => failed.has(label));
}

const LABEL_DESCRIPTIONS: Record<string, string> = {
  products: "products",
  balances: "stock levels",
  pos: "purchase orders",
  sos: "sales orders",
  transfers: "stock transfers",
  returns: "returns",
  shipments: "shipments",
  locations: "locations",
  ledger: "sales history",
  suppliers: "suppliers",
  soItems: "order lines",
  customers: "customers",
  memberships: "team members",
  invitations: "team invitations",
  orgSettings: "workspace settings",
  connectors: "integrations",
  exceptions: "integration errors",
  automationLogs: "automation",
  anomalies: "operational alerts",
  variants: "product variants",
};

/** Map an internal query label to operator-facing language. */
export function describeDashboardError(label: string): string {
  return LABEL_DESCRIPTIONS[label] ?? label;
}

/**
 * Dashboard must not treat "auth/membership not ready yet" as a data failure.
 * Organisation-scoped queries stay disabled until membership/role is resolved.
 */
export function dashboardWorkspaceSurface(input: {
  authLoading: boolean;
  orgId: string | null | undefined;
  queryLoading: boolean;
  queryEnabled: boolean;
  hasComputed: boolean;
  isError: boolean;
}): "loading" | "error" | "ready" {
  if (input.authLoading || !input.orgId) return "loading";
  if (!input.queryEnabled) return "loading";
  if (input.queryLoading) return "loading";
  if (input.hasComputed) return "ready";
  if (input.isError) return "error";
  return "loading";
}

export function dashboardQueryEnabled(authLoading: boolean, orgId: string | null | undefined): boolean {
  return !authLoading && !!orgId;
}